/**
 * AppPanel context — a tiny shared open/close signal for the running-app
 * control panel (MobileAppControlPanel). The TRIGGER lives in the header
 * (MobileHeader's cutout, when an app is running), but the panel itself is
 * rendered by MobileAppScreen — which holds the app-runtime ref and owns the
 * settings/share overlays. This context is the thread between the two sibling
 * surfaces, mirroring how useShell bridges the launcher and the app deck.
 */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

interface AppPanelValue {
  open: boolean
  /** Stable across renders (raw useState setter) — safe to use as an effect dep. */
  setOpen: (open: boolean) => void
  toggle: () => void
}

const AppPanelContext = createContext<AppPanelValue | null>(null)

export function AppPanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen((o) => !o), [])
  // Memoized so the value identity only changes when `open` does — consumers
  // that depend on `setOpen`/`toggle` (both stable) don't churn. Without this,
  // a "close on unmount" effect keyed on the value object fires its cleanup
  // every render and the panel closes the frame after it opens.
  const value = useMemo(() => ({ open, setOpen, toggle }), [open, toggle])
  return <AppPanelContext.Provider value={value}>{children}</AppPanelContext.Provider>
}

export function useAppPanel(): AppPanelValue {
  const ctx = useContext(AppPanelContext)
  if (!ctx) throw new Error('useAppPanel must be used within AppPanelProvider')
  return ctx
}
